import type { Slice } from '@atlaskit/editor-prosemirror/model';

/**
 * [CCI-17994] Meta carried on the transaction that applies a batch of REMOTE
 * agent-authored steps (BE streaming) in `editor-plugin-collab-edit`'s
 * `applyRemoteSteps`. It lets `editor-plugin-ai` record the agent's changes for a
 * Post Stream Review ("Review moment") WITHOUT collab-edit depending on the AI
 * plugin: the contract is neutral and lives here in `editor-common`, which both
 * plugins already depend on.
 *
 * Stamped whenever a received remote-step batch contains agent-authored steps.
 * Consumed by `aiSessionContextPlugin.apply()`,
 * which appends coarse `aiContentPositions` entries — exactly like the in-editor
 * (FE) streaming path's `AI_FINAL_STREAMING_TRANSFORMATION_DATA`, so the whole
 * downstream Review moment pipeline (merge → segment → diff → undo/redo → accept)
 * is reused unchanged.
 */
export const AGENT_REMOTE_EDIT_REVIEW_DATA = 'agentRemoteEditReviewData';

export interface AgentRemoteEditReviewSegment {
	/** Live-doc end of the change after the batch applied. A `remove` is zero-width (`endPos === startPos`). */
	endPos: number;
	/**
	 * Derived from the slice pair: empty original ⇒ `add`, empty new ⇒ `remove`,
	 * else `update`. Carried so the consumer does not re-derive it.
	 */
	kind: 'add' | 'remove' | 'update';
	/** Content now at [startPos, endPos] AFTER the batch (the agent's content; for redo). */
	newSlice: Slice;
	/** Content that was at the range BEFORE the batch (for undo / "compare with original"). */
	originalSlice: Slice;
	/** Live-doc start of the change after the batch applied. */
	startPos: number;
}

export interface AgentRemoteEditReviewData {
	/**
	 * AAID the agent acted on behalf of (the requester), read from the agent
	 * steps' `userId`. The AI plugin opens the Review moment only on the client
	 * whose local user matches this — so only the requester reviews. `undefined`
	 * when the batch carried no attributable user.
	 */
	actorUserId?: string;
	/**
	 * Identifier of the specific agent that authored the batch, from the agent
	 * steps' `agentId`. Additive on the steps contract, so it may be absent. Not
	 * consumed yet — carried so the contract is explicit, and so a future
	 * multi-agent Review moment can attribute each change to the agent that made
	 * it (`agentType` alone cannot distinguish two agents of the same kind).
	 */
	agentId?: string;
	/** The agent kind (e.g. `'mcp'` | `'twg'`), from the agent steps' `agentType`. */
	agentType?: string;
	/**
	 * Hybrid end-of-edit signal. When the BE/NCS marks a batch as the terminal one
	 * of an agent edit, the AI plugin opens the Review moment immediately instead of
	 * waiting for the idle debounce. `false`/absent ⇒ rely on the debounce.
	 */
	complete?: boolean;
	segments: AgentRemoteEditReviewSegment[];
}
